import { init as initErrorHandler } from '@/utils/error';
initErrorHandler();

import Vue from 'vue';
import DaySpanVuetify from 'dayspan-vuetify-2';
import 'dayspan-vuetify/dist/lib/dayspan-vuetify.min.css';
import Vuelidate from 'vuelidate';
import Amplify, { Auth } from 'aws-amplify';
import axios from 'axios';

import App from './App.vue';
import router from './router';
import store from './plugins/store.plugin';
import vuetify from './plugins/vuetify.plugin';
import { setUser, getImpersonationData, Tracking } from '@utils';
import '@/plugins/mixins.plugin';

Vue.use(Vuelidate);

const { VUE_APP_IDENTITY_POOL_ID, VUE_APP_USER_POOL_ID, VUE_APP_USER_POOL_WEB_CLIENT_ID, VUE_APP_DOMAIN, VUE_APP_APIURL } = process.env;

const identityPoolId = VUE_APP_IDENTITY_POOL_ID;
const userPoolId = VUE_APP_USER_POOL_ID;
const userPoolWebClientId = VUE_APP_USER_POOL_WEB_CLIENT_ID;
let domain = VUE_APP_DOMAIN;

Tracking.init();

Amplify.configure({
	Auth: {
		identityPoolId,
		region: 'eu-west-1',
		userPoolId,
		userPoolWebClientId,
		mandatorySignIn: false,
		cookieStorage: {
			domain,
			expires: 365,
			secure: false,
		},
		// OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
		//authenticationFlowType: 'USER_SRP_AUTH',
		oauth: {},
	},
});

Vue.use(Auth);

Vue.use(DaySpanVuetify, {
	// options is vue definition, the resulting reactive component is stored in components as this.$dayspan or Vue.$dayspan
	data: {
		// data or computed to override
	},
	computed: {
		// data or computed to override
	},
	methods: {
		// methods to override
	},
});

const initApp = async () => {
	try {
		const data = await Auth.currentAuthenticatedUser();

		store.commit('SET_COGNITO_USERGROUPS', data.signInUserSession.accessToken.payload['cognito:groups']);

		let isManager = await store.dispatch('IsInGroup', 'CompanyAdmin');
		let isLocum = await store.dispatch('IsInGroup', 'Locum');
		const isAdmin = await store.dispatch('IsInGroup', 'SuperAdmin');

		const { isImpersonatedLocum, isImpersonatedPractice } = getImpersonationData(isAdmin);
		const jwtToken = data.getSignInUserSession().getIdToken().jwtToken;

		//detect if we are hosted in our mobile app and register the push token;
		window.LocumbaseMobileApp = {
			onReceive: (msg, data) => {
				if (msg === 'push-token') {
					axios
						.post(
							VUE_APP_APIURL + '/notifications/addPushToken',
							{
								token: data.token,
								type: data.type,
							},
							{
								headers: { Authorization: jwtToken },
							}
						)
						//silently succeed or fail.
						.then(() => {})
						.catch(() => {});
				}
			},
		};
		if (window.ReactNativeWebView) {
			window.ReactNativeWebView.postMessage('get-push-token');
		}
		store.commit('SET_JWTTOKEN', jwtToken);
		store.commit('SET_USER', data.attributes); //commit empty so we dont have issues.

		const tenantId = data?.attributes?.['custom:tenant'];

		//Set theme
		const tenant = await store.dispatch('tenant/LoadTenant', tenantId);
		tenant.settings.theme.set(vuetify);

		Tracking.setUser(
			data?.attributes?.sub,
			data?.attributes?.email,
			data?.attributes?.name,
			data?.attributes?.family_name,
			data?.attributes?.locale,
			tenantId,
			data?.attributes?.profile
		);
		const isVerified = store.getters.getUserCompletedVerifiedStep;

		if (isManager || isImpersonatedPractice) {
			// Complete the entire cycle of setting a user. We already know that the user exists
			for await (const success of setUser('practice', data.attributes.zoneinfo || data.attributes.sub)) {
				if (!success && isVerified) {
					throw 'Problem setting practice user in initApp';
				}
			}
		} else if (isLocum || isImpersonatedLocum) {
			// Complete the entire cycle of setting a user. We already know that the user exists
			for await (const success of setUser('locum')) {
				if (!success && isVerified) {
					throw 'Problem setting locum user in initApp';
				}
			}
		}
		await store.dispatch('GET_CONFIGS_FROM_API');
		await store.dispatch('GET_INDUSTRIES_FROM_API', tenantId);
	} catch (err) {
		console.error(['unexpected error occured :', err]);
		// location.href = store.getters.getAUTH;
	}
};

export const bus = new Vue();

initApp().then(() => {
	new Vue({
		el: '#app',
		router,
		store,
		vuetify,
		render: h => h(App),
	});
});

Vue.config.productionTip = false;
